import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../../i18n/useP';
import { getTimeEffective, upsertTimeEffective } from '../../api/hrTimeEffective';
import { listEffectiveTime, upsertEffectiveTime } from '../../api/tdabc/effective_time';
import api from '../../api/client';
import EditableCell from '../hr/EditableCell';
import PersonTh from '../../components/PersonTh';
import Card from '../../components/Card';
import CardShell from '../../components/CardShell';
import SearchField from '../../components/SearchField';
import SegmentedToggle from '../../components/SegmentedToggle';
import FilterIcon from '../../components/FilterIcon';
import FilterCount from '../../components/FilterCount';
import PersonName from '../../components/PersonName';
import { formatNumber } from '../../utils/format';
import { normalizeName, buildSearchKey } from '../../utils/personTable';
import { useTableSort } from '../../hooks/useTableSort';
import { usePersistentValue } from '../../hooks/usePersistentState';
import '../hr/HrPage.css';

/* Heures TF — la même grille sert DEUX onglets (`part`, 2026-08-30) :

     'annual'  = l'ANNÉE : les quatre usages de la capacité pratique (TF, NF
                 client, absences) se saisissent, l'inutilisé s'en déduit.
                 Source `hr_time_effective_yearly`, la vérité du CCR.
     'monthly' = le DÉTAIL mensuel du seul TF (`tdabc_effective_time`), janvier
                 à novembre saisis, décembre calé sur l'annuel (Déc = Annuel −
                 Σ Jan..Nov, lecture seule) — sinon décembre se saisit comme
                 les autres.

   Les deux vivaient dans une seule table de dix-huit colonnes : on ne pouvait
   plus lire l'année sans traverser les mois. Le composant reste unique, il
   n'y a qu'un chargement et qu'une source. Pont annuel↔mensuel via
   `tdabc_people.hr_person_id`. */

const MONTH_NUMS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const FIELD = 'billable_hours';

// Payload complet préservé lors d'un upsert mensuel (on n'écrase que le facturable).
const zeroEntry = (personId, year, month) => ({
  person_id: personId, year, month,
  billable_hours: 0, nonbillable_hours: 0, corp_mgmt_hours: 0, pool_mgmt_hours: 0, absence_hours: 0, note: null,
});

/* Entête de colonne numérique triable — même costume que les autres : le
   libellé, puis sa flèche sur une seconde ligne. Un entête sans flèche est plus
   court d'une ligne et rompt l'alignement de toute la rangée. */
function SortTh({ col, label, sort, title = null, sep = false }) {
  const { sortCol, sortDir, handleSort } = sort;
  const on = sortCol === col;
  return (
    <th className={`hr-page__num hr-page__sort-th${sep ? ' hr-page__col-sep' : ''}${on ? ' is-sorted' : ''}`}
        onClick={() => handleSort(col)} title={title || undefined}>
      <span>{label}</span>
      <span className="hr-page__sort-ind">{on ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</span>
    </th>
  );
}

export default function ProductionTimeGrid({ activeTenant, addToast, year, part = 'monthly' }) {
  const { t } = useTranslation();
  const p = useP();
  const tid = activeTenant?.id;
  const MONTHS = useMemo(
    () => Array.from({ length: 12 }, (_, i) => t(`common:months.${i + 1}`)),
    [t],
  );

  const [rows, setRows] = useState([]);          // lignes annuelles (hr) : spine
  const [tdabcByHr, setTdabcByHr] = useState({});// hr_person_id → tdabc person_id
  const [monthly, setMonthly] = useState({});    // tdabcId → { month → entry }
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  // « Actif » = statut backend (contrat ouvert à ce jour), porté par les lignes annuelles.
  const [status, setStatus] = usePersistentValue(`tdabc_prodtime_status_v2_${tid}`, 'all');
  const sort = useTableSort(`tdabc_prodtime_sort_${tid}`, 'last_name_sort');
  const { sortCol, sortDir, handleSort, sortRows, nameOrder } = sort;

  const refresh = () => {
    if (!tid || !year) { setRows([]); setMonthly({}); setTdabcByHr({}); return; }
    setLoading(true);
    Promise.all([
      // Personnel DIRECT uniquement : on exclut les pools structure/overhead (ex. Administration).
      getTimeEffective(tid, year).then(r => (r?.data?.rows || []).filter(x => !x.is_overhead_pool)),
      api.get(`/tdabc/people/${tid}`).then(r => r.data?.data || r.data || []),
      listEffectiveTime(tid, year).then(r => r.data || []),
    ])
      .then(([hrRows, tPeople, entries]) => {
        setRows(hrRows);
        const map = {};
        for (const person of tPeople) if (person.hr_person_id) map[person.hr_person_id] = person.id;
        setTdabcByHr(map);
        const lk = {};
        for (const e of entries) {
          (lk[e.person_id] || (lk[e.person_id] = {}))[e.month] = e;
        }
        setMonthly(lk);
      })
      .catch(e => addToast?.(e?.response?.data?.detail || e?.message || p('Échec du chargement'), 'error'))
      .finally(() => setLoading(false));
  };
  useEffect(refresh, [tid, year]); // eslint-disable-line

  // ── Accès valeurs ──
  const monthVal = (tdabcId, m) => Number(monthly[tdabcId]?.[m]?.[FIELD] || 0);
  const sumJanNov = (tdabcId) => MONTH_NUMS.slice(0, 11).reduce((s, m) => s + monthVal(tdabcId, m), 0);

  // ── Saisie annuelle (hr_time_effective_yearly) ──
  const saveAnnual = async (hrId, val) => {
    setRows(prev => prev.map(r => r.person_id === hrId ? { ...r, [FIELD]: val } : r));
    const r = await upsertTimeEffective(tid, year, hrId, { [FIELD]: val });
    if (r.error) { addToast?.(r.error, 'error'); }
    refresh();   // les deux soldes sont calculés au serveur : ils suivent la saisie
  };

  // ── Saisie annuelle d'un champ quelconque (TF, NF client, absences) ──
  const saveField = async (hrId, field, val) => {
    setRows(prev => prev.map(r => r.person_id === hrId ? { ...r, [field]: val } : r));
    const r = await upsertTimeEffective(tid, year, hrId, { [field]: val });
    if (r.error) { addToast?.(r.error, 'error'); }
    refresh();   // l'inutilisé est un solde : il se recalcule au serveur
  };

  // ── Saisie mensuelle (tdabc_effective_time) ──
  const saveMonth = async (tdabcId, month, val) => {
    if (!tdabcId) { addToast?.(p('Personne sans lien mensuel (tdabc) — saisie mensuelle indisponible.'), 'error'); return; }
    const existing = monthly[tdabcId]?.[month] || zeroEntry(tdabcId, year, month);
    const payload = { ...existing, [FIELD]: val };
    setMonthly(prev => ({ ...prev, [tdabcId]: { ...(prev[tdabcId] || {}), [month]: payload } }));
    try {
      const res = await upsertEffectiveTime(tid, tdabcId, year, month, payload);
      const updated = res.data;
      setMonthly(prev => ({ ...prev, [tdabcId]: { ...(prev[tdabcId] || {}), [month]: updated } }));
    } catch (e) {
      addToast?.(e?.response?.data?.detail || e?.message || p('Échec de la saisie mensuelle'), 'error');
      refresh();
    }
  };

  const view = useMemo(() => {
    const q = normalizeName(filter.trim());
    const enriched = rows.map(r => {
      const tdabcId = tdabcByHr[r.person_id];
      const annual = Number(r[FIELD] || 0);
      const total = annual > 0
        ? annual
        : MONTH_NUMS.reduce((s, m) => s + Number(monthly[tdabcId]?.[m]?.[FIELD] || 0), 0);
      return {
        ...r,
        person_name: `${r.first_name || ''} ${r.last_name || ''}`.trim(),
        first_name_sort: normalizeName(r.first_name),
        last_name_sort: normalizeName(r.last_name),
        _search: buildSearchKey(r.first_name, r.last_name, r.cost_pool),
        _total: total,
        _overCap: Math.max(0, Number(r.idle_hours || 0)),
        _overProd: Math.max(0, -Number(r.idle_hours || 0)),
      };
    });
    const filtered = enriched.filter(r => {
      if (status === 'active' && !(r.status === 'active' || r.status === 'joined')) return false;
      if (q && !r._search.includes(q)) return false;
      return true;
    });
    return sortRows(filtered);
  }, [rows, filter, status, sortCol, sortDir, tdabcByHr, monthly]); // eslint-disable-line

  if (!tid) return <div className="hr-page__empty">{p('Sélectionner une entreprise.')}</div>;

  /* Standard de carte : les deux contrôles RESTREIGNENT les lignes, ils vivent
     donc dans la toolbar, la recherche fermant la ligne à droite. */
  const activeFilters = (status === 'active' ? 1 : 0) + (filter.trim() ? 1 : 0);

  // ── Totaux de pied ──
  const totalTr = view.reduce((s, r) => s + Number(r.tr_hours || 0), 0);
  const totalAnnual = view.reduce((s, r) => s + Number(r[FIELD] || 0), 0);
  const totalNf = view.reduce((s, r) => s + Number(r.nonbillable_hours || 0), 0);
  const totalAbs = view.reduce((s, r) => s + Number(r.absence_hours || 0), 0);
  const totalProj = view.reduce((s, r) => s + Number(r.project_hours || 0), 0);
  /* Les deux soldes se totalisent SÉPARÉMENT : les compenser masquerait à la
     fois la capacité dormante et le dépassement. */
  const totalOverCap = view.reduce((s, r) => s + Math.max(0, Number(r.idle_hours || 0)), 0);
  const totalOverProd = view.reduce((s, r) => s + Math.max(0, -Number(r.idle_hours || 0)), 0);

  return (
    <CardShell
      flush
      title={<>{part === 'annual' ? p('Usage annuel de la capacité') : p('Heures TF par mois')} <small>· {year} · {view.length} {p('personne(s)')}</small></>}
      /* Pas de sous-titre sur l'annuel : les bandeaux TF / NF disent déjà ce
         que la capacité devient. */
      subtitle={part === 'annual' ? null : t('phrases:p_d23e191f56')}
      filters={(
        <>
          <FilterIcon />
          <SegmentedToggle
            compact
            label={p('Statut')} value={status} onChange={setStatus}
            options={[
              { value: 'all', label: p('Tous') },
              { value: 'active', label: p('Actifs'), tone: 'alert' },
            ]}
          />
          <FilterCount count={activeFilters}
                       onClear={() => { setStatus('all'); setFilter(''); }} />
        </>
      )}
      search={(
        <SearchField value={filter} onChange={setFilter}
                     placeholder={p('Personne ou groupe…')} />
      )}
      filterCount={activeFilters}
      collapsible storageKey={part === 'annual' ? 'heures-tf-annuel' : 'heures-tf-mensuel'}
    >
      {loading && <div className="hr-page__loading">{t('common:status.loading')}</div>}
      {!loading && (
        <div className="hr-page__table-wrap" style={{ overflowX: 'auto' }}>
          <table className={`hr-page__table${part === 'annual' ? ' ptg-annual' : ''}`}>
            <thead>
              {/* Les quatre colonnes qui suivent le facturable disent la même
                  chose : le temps NON facturable, et ce qu'il devient. Un
                  bandeau les rassemble plutôt que de laisser lire quatre
                  colonnes sans lien apparent (2026-08-30). */}
              {part === 'annual' && (
                <tr>
                  <th className="hr-page__group-th is-empty" colSpan={3} />
                  <th className="hr-page__group-th hr-page__group-th--tf">{p('Temps TF')}</th>
                  <th className="hr-page__group-th hr-page__col-sep" colSpan={5}>{p('Temps NF')}</th>
                </tr>
              )}
              <tr>
                <PersonTh sort={sort} className="hr-page__sort-th" />
                <th className={`hr-page__sort-th${sortCol === 'cost_pool' ? ' is-sorted' : ''}`} onClick={() => handleSort('cost_pool')} title={p('Cliquer pour trier')}>
                  <span>{p('Groupe')}</span>
                  <span className="hr-page__sort-ind">{sortCol === 'cost_pool' ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</span>
                </th>
                <th className={`hr-page__num hr-page__sort-th${sortCol === 'tr_hours' ? ' is-sorted' : ''}`} onClick={() => handleSort('tr_hours')} title={t('phrases:p_f8d284d2bc')}>
                  <span>{t('phrases:p_91644115a8')}</span>
                  <span className="hr-page__sort-ind">{sortCol === 'tr_hours' ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</span>
                </th>
                {/* La colonne « Standard » (cible d'heures productives) est
                    retirée le 2026-08-30 : cet onglet dit ce que la capacité
                    EST DEVENUE, pas ce qu'on visait. Le champ reste en base et
                    se saisit ailleurs. */}
                <th className={`hr-page__num hr-page__col-sep hr-page__sort-th${sortCol === FIELD ? ' is-sorted' : ''}`} onClick={() => handleSort(FIELD)} title={t('phrases:p_8b81d6277e')}>
                  <span>{part === 'annual' ? p('Heures facturables') : p('Annuel')}</span>
                  <span className="hr-page__sort-ind">{sortCol === FIELD ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</span>
                </th>
                {part === 'annual' ? (
                  <>
                    {/* Toutes triables, comme les colonnes voisines : un entête
                        sans sa flèche est plus court d'une ligne, et les
                        libellés cessent de s'aligner (2026-08-30). */}
                    <SortTh col="nonbillable_hours" label={p('NF client')} sort={sort} sep />
                    <SortTh col="project_hours" label={p('Temps projets')} sort={sort}
                            title={p('Projets internes : développement, outillage, R&D. Ni facturés, ni non-facturés client.')} />
                    <SortTh col="absence_hours" label={p('Absences')} sort={sort} />
                    {/* Le solde a DEUX sens (2026-08-30) : positif, il reste de
                        la capacité ; négatif, on a affecté plus d'heures que la
                        capacité pratique n'en offre. Deux colonnes plutôt qu'un
                        nombre signé — un « −34 » au milieu d'heures se lit mal,
                        et les deux ne se somment pas. */}
                    <SortTh col="_overProd" label={p('Sur-production')} sort={sort}
                            title={p('Heures affectées AU-DELÀ de la capacité pratique.')} />
                    <SortTh col="_overCap" label={p('Capacité non utilisée')} sort={sort}
                            title={p('Capacité pratique non utilisée : TR moins facturé, non facturé, projets et absences.')} />
                  </>
                ) : (
                  <>
                    {MONTHS.map((m, i) => (
                      <th key={i} className={`hr-page__num${i === 0 ? ' hr-page__col-sep' : ''}`}>{m}</th>
                    ))}
                    <th className={`hr-page__num hr-page__sort-th${sortCol === '_total' ? ' is-sorted' : ''}`} onClick={() => handleSort('_total')} title={p('Cliquer pour trier')}>
                      <span>{p('Total')}</span>
                      <span className="hr-page__sort-ind">{sortCol === '_total' ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</span>
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {view.length === 0 && <tr><td colSpan={18} className="hr-page__empty">{p('Aucune personne.')}</td></tr>}
              {view.map(r => {
                const tdabcId = tdabcByHr[r.person_id];
                const annual = Number(r[FIELD] || 0);
                const annualDriven = annual > 0;
                const janNov = sumJanNov(tdabcId);
                const decVal = annualDriven ? Math.max(0, annual - janNov) : monthVal(tdabcId, 12);
                const total = annualDriven ? annual : MONTH_NUMS.reduce((s, m) => s + monthVal(tdabcId, m), 0);
                return (
                  <tr key={r.person_id}>
                    <td><PersonName firstName={r.first_name} lastName={r.last_name} order={nameOrder} /></td>
                    <td className={r.cost_pool ? '' : 'hr-page__muted'}>{r.cost_pool || '—'}</td>
                    <td className="hr-page__num hr-page__muted">{formatNumber(Number(r.tr_hours || 0), 0)}</td>

                    <td className="hr-page__num hr-page__col-sep">
                      <EditableCell value={annual || ''} type="number" align="right"
                        onCommit={v => saveAnnual(r.person_id, v ?? 0)} />
                    </td>
                    {part === 'annual' ? (
                      <>
                        <td className="hr-page__num hr-page__col-sep">
                          <EditableCell value={Number(r.nonbillable_hours || 0) || ''} type="number" align="right"
                            onCommit={v => saveField(r.person_id, 'nonbillable_hours', v ?? 0)} />
                        </td>
                        <td className="hr-page__num">
                          <EditableCell value={Number(r.project_hours || 0) || ''} type="number" align="right"
                            onCommit={v => saveField(r.person_id, 'project_hours', v ?? 0)} />
                        </td>
                        <td className="hr-page__num">
                          <EditableCell value={Number(r.absence_hours || 0) || ''} type="number" align="right"
                            onCommit={v => saveField(r.person_id, 'absence_hours', v ?? 0)} />
                        </td>
                        {/* L'inutilisé est un SOLDE, jamais une saisie : le
                            calculer côté serveur évite deux vérités. */}
                        <td className="hr-page__num hr-page__muted">
                          {r._overProd > 0 ? formatNumber(r._overProd, 0) : '—'}
                        </td>
                        <td className="hr-page__num hr-page__muted">
                          {r._overCap > 0 ? formatNumber(r._overCap, 0) : '—'}
                        </td>
                      </>
                    ) : (
                      <>
                        {MONTH_NUMS.map(m => {
                          // Filet de fermeture du bloc Annuel (porté par janvier).
                          const sep = m === 1 ? ' hr-page__col-sep' : '';
                          // Décembre piloté par l'annuel = lecture seule (plug).
                          if (m === 12 && annualDriven) {
                            return <td key={m} className={`hr-page__num hr-page__muted${sep}`} title={t('phrases:p_ffa236c278')}>{formatNumber(decVal, 0)}</td>;
                          }
                          const val = monthVal(tdabcId, m);
                          return (
                            <td key={m} className={`hr-page__num${sep}`}>
                              <EditableCell value={val || ''} type="number" align="right"
                                onCommit={v => saveMonth(tdabcId, m, v ?? 0)} />
                            </td>
                          );
                        })}
                        <td className="hr-page__num"><strong>{formatNumber(total, 0)}</strong></td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={2}><strong>{p('Total')}</strong></td>
                <td className="hr-page__num"><strong>{formatNumber(totalTr, 0)}</strong></td>

                <td className="hr-page__num hr-page__col-sep"><strong>{formatNumber(totalAnnual, 0)}</strong></td>
                {part === 'annual' ? (
                  <>
                    <td className="hr-page__num hr-page__col-sep"><strong>{formatNumber(totalNf, 0)}</strong></td>
                    <td className="hr-page__num"><strong>{formatNumber(totalProj, 0)}</strong></td>
                    <td className="hr-page__num"><strong>{formatNumber(totalAbs, 0)}</strong></td>
                    <td className="hr-page__num"><strong>{formatNumber(totalOverCap, 0)}</strong></td>
                    <td className="hr-page__num"><strong>{formatNumber(totalOverProd, 0)}</strong></td>
                  </>
                ) : (
                  <td className="hr-page__col-sep" colSpan={13} />
                )}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </CardShell>
  );
}
